// src/MissingEmbellishments.jsx — "Missing embellishments" warning (Eric, 2026-10-02,
// SP-336682:A). The order-detail API returns `order.missing_required`: required
// embellishments (from the garments' own `_Required SKU` attributes) that are not on
// the order. Team Portal edit orders (":A") cannot be edited by OpsHub, so they can be
// neither fixed nor tagged "Broken Order" — this warning makes the gap visible anyway,
// so production does not press and ship an order that is short a logo or a number.
//
// Two renderings of the same data: a Polaris Banner for the order page, and a plain
// high-contrast block for the Pressing Portal kiosk.
import React from 'react';
import { Banner, BlockStack, Text, List } from '@shopify/polaris';

const line = m => {
    // Embellishment titles usually start with their own SKU ("210FC-1 - - 210 FC …"); don't print it twice.
    const name = !m.title ? m.sku : (m.title.toUpperCase().startsWith(String(m.sku).toUpperCase()) ? m.title : `${m.title} (${m.sku})`);
    const forWhat = (m.for || []).length ? ` — needed for ${m.for.join(', ')}` : '';
    return { name, qty: m.qty, forWhat };
};
const totalUnits = list => list.reduce((a, m) => a + (Number(m.qty) || 0), 0);

export function MissingEmbellishmentsBanner({ missing }) {
    if (!Array.isArray(missing) || missing.length === 0) return null;
    const n = totalUnits(missing);
    return (
        <div style={{ marginBottom: 12 }}>
            <Banner tone="critical" title={`Missing embellishments: ${n} ${n === 1 ? 'item is' : 'items are'} required but not on this order`}>
                <BlockStack gap="200">
                    <List>
                        {missing.map(m => { const l = line(m); return (
                            <List.Item key={m.sku}><Text as="span" fontWeight="bold">{l.qty} × {l.name}</Text>{l.forWhat}</List.Item>
                        ); })}
                    </List>
                    <Text as="p" variant="bodyMd">
                        Do not press or ship until the embellishments are verified. This order could not be fixed.
                    </Text>
                </BlockStack>
            </Banner>
        </div>
    );
}

export function MissingEmbellishmentsKiosk({ missing, t = s => s }) {
    if (!Array.isArray(missing) || missing.length === 0) return null;
    const n = totalUnits(missing);
    return (
        <div role="alert" style={{ background: '#fde2e1', border: '2px solid #c5280c', borderRadius: 8, padding: '10px 14px', margin: '8px 0', color: '#5b1408' }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                {t('Missing embellishments')}: {n} {t('required, not on this order')}
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.5 }}>
                {missing.map(m => { const l = line(m); return (
                    <li key={m.sku}><strong>{l.qty} × {l.name}</strong>{l.forWhat}</li>
                ); })}
            </ul>
            <div style={{ fontSize: 13, marginTop: 6, fontWeight: 600 }}>{t('Stop and tell a manager. Do not press or ship until the embellishments are verified. This order could not be fixed.')}</div>
        </div>
    );
}
